import React from 'react';
import styled from 'styled-components';
import { Formik, yupToFormErrors } from 'formik';
import * as Yup from 'yup';

import { getTranslatedString } from 'helpers/i18n';

import Col from 'components/ui/Col';
import Spacer from 'components/ui/Spacer';
import Row from 'components/ui/Row';
import { RenderIfToggleOn } from 'components/shared/ToggleRenderer';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';

import Type from './fields/Type';
import NoticeName from './fields/NoticeName';
import Name from './fields/Name';
import NoticeDescription from './fields/Description';
import Delivery from './fields/Delivery';
import Assignment from './fields/Assignment';

import Properties from './fields/Properties';
import AllowAttachmentModify from './fields/AllowAttachmentModify';
import AllowImageModify from './fields/AllowImageModify';
import NoticeImage from './fields/NoticeImage';
import NoticeAttachment from './fields/NoticeAttachment';

const StyledTitle = styled.span`
  font-size: 18px;
  font-family: 'Circular-Bold';
`;
const StyledColMb8 = styled(Col)`
  margin-bottom: 8px;
`;
const StyledSectionContainer = styled.div`
  margin-bottom: 32px;
`;

const NoticeTemplateForm = ({ formName, formData, handleSubmit }) => {
  const validateForm = async (values) => {
    const requiredErrorVerbiage = getTranslatedString('required');
    let validationSchema = {
      notice_type: Yup.string().required(requiredErrorVerbiage),

      // properties only required if assign_to_properties === true
      properties: Yup.array().test('properties', requiredErrorVerbiage, (properties) => {
        if (!values.assign_to_properties) return true;
        return properties.length !== 0;
      }),

      notice_name: Yup.string().required(requiredErrorVerbiage),
      name: Yup.string().required(requiredErrorVerbiage),
      notice_description: Yup.string().required(requiredErrorVerbiage),
    };
    const schema = Yup.object().shape(validationSchema);
    return schema.validate(values, { abortEarly: false }).catch((error) => {
      throw yupToFormErrors(error);
    });
  };

  const initialValues = {
    id: formData.id || undefined,
    notice_template_id: formData.notice_template_id || undefined,
    notice_type: formData.notice_type || undefined,
    attachments: formData.attachments || [],
    new_attachments: formData.new_attachments || [],
    properties: formData.properties || [],
    name: formData.name || undefined,
    notice_name: formData.notice_name || undefined,
    notice_description: formData.notice_description || undefined,
    assign_to_properties: formData.assign_to_properties || false,
    allow_image_modify: [true, false].includes(formData.allow_image_modify)
      ? formData.allow_image_modify
      : true,
    allow_attachment_modify: [true, false].includes(formData.allow_attachment_modify)
      ? formData.allow_attachment_modify
      : true,
    notice_image: formData.notice_image || undefined,
    remove_image: formData.remove_image || false,
    delivery: formData.delivery || 'any',
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validateForm}
      enableReinitialize
    >
      {({ handleSubmit, ...rest }) => {
        return (
          <VerticalFormWrapper id={formName} onSubmit={handleSubmit}>
            <StyledSectionContainer>
              <Spacer mb={2}>
                <Row gutter={16}>
                  <StyledColMb8 span={24}>
                    <StyledTitle>
                      {getTranslatedString('landlordPortal.noticeForm.type')}
                    </StyledTitle>
                  </StyledColMb8>
                  <Col span={24}>
                    <Spacer mb={1}>
                      <Type {...rest} />
                    </Spacer>
                  </Col>
                </Row>
              </Spacer>

              <Spacer mb={2}>
                <Row gutter={16}>
                  <StyledColMb8 span={24}>
                    <StyledTitle>
                      {getTranslatedString('landlordPortal.noticeForm.noticeDetails')}
                    </StyledTitle>
                  </StyledColMb8>
                  <Col span={24}>
                    <Spacer mb={1}>
                      <Name {...rest} />
                    </Spacer>
                    <Spacer mb={1}>
                      <NoticeName {...rest} />
                    </Spacer>
                    <Spacer mb={1}>
                      <NoticeDescription {...rest} />
                    </Spacer>
                  </Col>
                </Row>
              </Spacer>
              <RenderIfToggleOn toggle={'notice_restrictions'}>
                <Spacer mb={2}>
                  <Row gutter={16}>
                    <StyledColMb8 span={24}>
                      <StyledTitle>
                        {getTranslatedString('landlordPortal.noticeTemplateForm.deliveryTitle')}
                      </StyledTitle>
                    </StyledColMb8>
                    <Col span={24}>
                      <Spacer mb={1}>
                        <Delivery {...rest} />
                      </Spacer>
                    </Col>
                  </Row>
                </Spacer>
              </RenderIfToggleOn>

              <RenderIfToggleOn toggle={'notice_restrictions'}>
                <Spacer mb={2}>
                  <Row gutter={16}>
                    <StyledColMb8 span={24}>
                      <StyledTitle>
                        {getTranslatedString('landlordPortal.noticeTemplateForm.assignmentTitle')}
                      </StyledTitle>
                    </StyledColMb8>
                    <Col span={24}>
                      <Spacer mb={1}>
                        <Assignment {...rest} />
                      </Spacer>
                      <Spacer mb={1}>
                        <Properties {...rest} />
                      </Spacer>
                    </Col>
                  </Row>
                </Spacer>
              </RenderIfToggleOn>

              <Spacer mb={2}>
                <Row gutter={16}>
                  <StyledColMb8 span={24}>
                    <StyledTitle>
                      {getTranslatedString('landlordPortal.noticeForm.attachments')}
                    </StyledTitle>
                  </StyledColMb8>
                  <Col span={24}>
                    <Spacer mb={1}>
                      <NoticeAttachment {...rest} />
                    </Spacer>

                    <RenderIfToggleOn toggle="notice_restrictions">
                      <Spacer mb={1}>
                        <AllowAttachmentModify {...rest} />
                      </Spacer>
                    </RenderIfToggleOn>
                  </Col>
                </Row>
              </Spacer>

              <Spacer mb={2}>
                <Row gutter={16}>
                  <StyledColMb8 span={24}>
                    <StyledTitle>
                      {getTranslatedString('landlordPortal.noticeTemplateForm.photo')}
                    </StyledTitle>
                  </StyledColMb8>
                  <Col span={24}>
                    <Spacer mb={1}>
                      <NoticeImage {...rest} />
                    </Spacer>

                    <RenderIfToggleOn toggle="notice_restrictions">
                      <Spacer mb={1}>
                        <AllowImageModify {...rest} />
                      </Spacer>
                    </RenderIfToggleOn>
                  </Col>
                </Row>
              </Spacer>
            </StyledSectionContainer>
          </VerticalFormWrapper>
        );
      }}
    </Formik>
  );
};

export default NoticeTemplateForm;
